import React from 'react';
import './CompareWarnings.css';
import { Files, Clock, VolumeX, Film, Scissors, Shuffle, AlertTriangle } from 'lucide-react';

/**
 * CompareWarnings - a single consolidated list of comparison issues, gathered
 * from all the per-side analyses ComparePage already holds. Per-side detail
 * panels (FileSummary / FrameQC / CutScan) stay below; this is the summary.
 */

const ICONS = {
    samefile: Files,
    length: Clock,
    audio: VolumeX,
    blank: Film,
    cuts: Scissors,
    jumps: Shuffle,
};

// --- pure builder (unit-tested) ----------------------------------------------

/**
 * @returns {Array<{ id, severity:'warn'|'info', kind, title, detail }>}
 */
export function buildCompareWarnings({ sameness, beforeMeta, afterMeta, beforeQC, afterQC, beforeCut, afterCut } = {}) {
    const warnings = [];

    // 1. Same / identical file
    if (sameness === 'same-file') {
        warnings.push({
            id: 'samefile', severity: 'warn', kind: 'samefile',
            title: 'Same file on both sides',
            detail: "The same file was dropped on both sides — there's nothing to compare.",
        });
    } else if (sameness === 'identical-content') {
        warnings.push({
            id: 'samefile', severity: 'warn', kind: 'samefile',
            title: 'Identical files',
            detail: 'Both files are byte-for-byte identical (different names).',
        });
    }

    const bothVideo = isVideo(beforeMeta) && isVideo(afterMeta);

    // 2. Length mismatch
    if (bothVideo) {
        const fb = frameCount(beforeMeta), fa = frameCount(afterMeta);
        const db = durationSec(beforeMeta), da = durationSec(afterMeta);
        const framesDiffer = fb != null && fa != null && fb !== fa;
        const durDiffer = db != null && da != null && Math.abs(db - da) > 0.05;
        if (framesDiffer || durDiffer) {
            warnings.push({
                id: 'length', severity: 'warn', kind: 'length',
                title: 'Length mismatch',
                detail: `Before ${lenStr(fb, db)} · After ${lenStr(fa, da)}`,
            });
        }
    }

    // 3. Audio — mismatch (prominent) or both-missing (note)
    if (bothVideo) {
        const ab = hasAudio(beforeMeta), aa = hasAudio(afterMeta);
        if (ab !== aa) {
            warnings.push({
                id: 'audio', severity: 'warn', kind: 'audio',
                title: 'Audio mismatch',
                detail: `${ab ? 'After' : 'Before'} has no audio track.`,
            });
        } else if (!ab && !aa) {
            warnings.push({
                id: 'audio', severity: 'info', kind: 'audio',
                title: 'No audio',
                detail: 'Neither file has an audio track.',
            });
        }
    }

    // 4. Blank / black frames
    const blankParts = [];
    const bBlank = blankDesc(beforeQC); if (bBlank) blankParts.push(`Before: ${bBlank}`);
    const aBlank = blankDesc(afterQC); if (aBlank) blankParts.push(`After: ${aBlank}`);
    if (blankParts.length) {
        warnings.push({
            id: 'blank', severity: 'warn', kind: 'blank',
            title: 'Blank / black frames',
            detail: blankParts.join(' · '),
        });
    }

    // 5. Cuts (warn) + experimental jump cuts (info)
    const bHard = hardCount(beforeCut), aHard = hardCount(afterCut);
    if (bHard > 0 || aHard > 0) {
        const parts = [];
        if (beforeCut?.scanned) parts.push(`Before: ${bHard ? `${bHard} cut${bHard === 1 ? '' : 's'}` : 'continuous'}`);
        if (afterCut?.scanned) parts.push(`After: ${aHard ? `${aHard} cut${aHard === 1 ? '' : 's'}` : 'continuous'}`);
        warnings.push({
            id: 'cuts', severity: 'warn', kind: 'cuts',
            title: 'Cuts detected (not a continuous take)',
            detail: parts.join(' · '),
        });
    }

    const bJump = jumpCount(beforeCut), aJump = jumpCount(afterCut);
    if (bJump > 0 || aJump > 0) {
        const parts = [];
        if (bJump > 0) parts.push(`Before: ${bJump}`);
        if (aJump > 0) parts.push(`After: ${aJump}`);
        warnings.push({
            id: 'jumps', severity: 'info', kind: 'jumps',
            title: 'Possible jump cuts (experimental)',
            detail: `${parts.join(' · ')} — low confidence`,
        });
    }

    return warnings;
}

function isVideo(m) { return !!(m?.fileType && m.fileType.startsWith('video/')); }
function frameCount(m) { return m?.videoMetadata?.Video?.numberOfSamples ?? m?.AllTracks?.[0]?.numberOfSamples ?? null; }
function durationSec(m) {
    const d = m?.videoMetadata?.General?.durationSeconds;
    if (d != null) return d;
    return m?.duration ? parseFloat(String(m.duration)) : null;
}
function hasAudio(m) { return !!(m?.videoMetadata?.Audio || m?.Audio); }
function lenStr(frames, dur) {
    const parts = [];
    if (frames != null) parts.push(`${frames.toLocaleString()} frames`);
    if (dur != null) parts.push(`${dur.toFixed(2)}s`);
    return parts.join(' / ') || 'unknown';
}
function hardCount(cut) { return cut?.scanned ? cut.cuts.filter((c) => c.type === 'hard').length : 0; }
function jumpCount(cut) { return cut?.scanned ? cut.cuts.filter((c) => c.type === 'jump').length : 0; }
function typeWord(t) { return t === 'black' ? 'black' : t === 'flat' ? 'blank' : 'black/blank'; }
function blankDesc(qc) {
    if (!qc || !qc.decodable) return null;
    if (qc.wholeClip) return 'entire clip blank';
    const parts = [];
    if (qc.head) parts.push(`${qc.head.count} leading ${typeWord(qc.head.type)}`);
    if (qc.tail) parts.push(`${qc.tail.count} trailing ${typeWord(qc.tail.type)}`);
    return parts.length ? parts.join(', ') : null;
}

// --- presentational ----------------------------------------------------------

function CompareWarnings({ warnings }) {
    if (!warnings || warnings.length === 0) return null;
    return (
        <div className="compare-warnings">
            <div className="compare-warnings-header">
                <AlertTriangle size={13} className="compare-warnings-header-icon" />
                <span>Comparison Warnings</span>
                <span className="compare-warnings-count">{warnings.length}</span>
            </div>
            {warnings.map((w) => {
                const Icon = ICONS[w.kind] || AlertTriangle;
                return (
                    <div key={w.id} className={`compare-warning compare-warning-${w.severity}`}>
                        <Icon size={15} className="compare-warning-icon" />
                        <div className="compare-warning-text">
                            <span className="compare-warning-title">{w.title}</span>
                            {w.detail && <span className="compare-warning-detail">{w.detail}</span>}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default CompareWarnings;
